import React from "react";
import { Text } from "@nextui-org/react";
import moment from "moment";

const BanksLedgerContent = ({
  banks = [],
  entriesMap = {},
  payments = [],
  grandTotal = 0,
  totalPaid = 0,
  finalBalance = 0,
}) => {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* 1. BANK SECTIONS */}
      {banks.map((bank) => {
        const bankEntries = entriesMap[bank._id] || [];
        const bankTotal = bankEntries.reduce(
          (sum, e) => sum + (Number(e.amount) || 0),
          0,
        );

        return (
          <div
            key={bank._id}
            style={{
              width: "100%",
              backgroundColor: "#ffffff",
            }}
          >
            {/* Bank Header Row - Subtle Slate Tint */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 16px",
                backgroundColor: "#f1f5f9",
                borderTop: "1px solid #cbd5e1",
                borderBottom: "1px solid #cbd5e1",
              }}
            >
              <Text
                b
                size="13px"
                css={{
                  margin: 0,
                  color: "#0f172a",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {bank.name}
              </Text>
              <Text
                size="12px"
                css={{ margin: 0, color: "#64748b", fontWeight: 600 }}
              >
                {bankEntries.length}{" "}
                {bankEntries.length === 1 ? "entry" : "entries"}
              </Text>
            </div>

            {/* List of Entries divided with Dividers */}
            {bankEntries.length === 0 ? (
              <div
                style={{
                  padding: "10px 16px",
                  textAlign: "center",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <Text size="12px" color="$accents6" css={{ margin: 0 }}>
                  No entries for this bank in selected month
                </Text>
              </div>
            ) : (
              <div>
                {bankEntries.map((entry) => (
                  <div
                    key={entry._id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 16px",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        textAlign: "left",
                        gap: "1px",
                        overflow: "hidden",
                        paddingRight: "12px",
                      }}
                    >
                      <Text
                        b
                        size="12px"
                        css={{
                          margin: 0,
                          color: "#1e293b",
                          lineHeight: 1.25,
                          wordBreak: "break-word",
                          textAlign: "left !important",
                        }}
                      >
                        {entry.reason}
                      </Text>
                      <Text
                        size="11px"
                        css={{
                          margin: 0,
                          color: "#64748b",
                          textAlign: "left !important",
                        }}
                      >
                        {entry.created
                          ? moment(entry.created).format("DD MMM, hh:mm A")
                          : "-"}
                      </Text>
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Text
                        b
                        size="13px"
                        css={{
                          margin: 0,
                          color: entry.amount >= 0 ? "#16a34a" : "#dc2626",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {entry.amount >= 0 ? "+" : ""}
                        &#8377;
                        {Math.abs(Number(entry.amount)).toLocaleString(
                          "en-IN",
                          {
                            minimumFractionDigits: 2,
                          },
                        )}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bank Total Row - Justified Apart with Subtle Soft Slate Tint */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 16px",
                backgroundColor: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
                borderBottom: "1px solid #cbd5e1",
              }}
            >
              <Text
                b
                size="13px"
                css={{ margin: 0, color: "#1e293b", fontWeight: 700 }}
              >
                {bank.name} Total
              </Text>
              <Text
                b
                size="14px"
                css={{
                  margin: 0,
                  color: bankTotal >= 0 ? "#16a34a" : "#dc2626",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 700,
                }}
              >
                {bankTotal >= 0 ? "+" : ""}
                &#8377;
                {bankTotal.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </Text>
            </div>
          </div>
        );
      })}

      {/* 2. GRAND TOTAL SECTION - Subtle Soft Sky Blue Tint */}
      <div
        style={{
          width: "100%",
          backgroundColor: "#f0f9ff",
          borderTop: "1px solid #bae6fd",
          borderBottom: "1px solid #bae6fd",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text
          b
          size="13px"
          css={{
            margin: 0,
            color: "#0369a1",
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          Grand Total (All Banks)
        </Text>
        <Text
          b
          size="15px"
          css={{
            margin: 0,
            color: grandTotal >= 0 ? "#0369a1" : "#dc2626",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 700,
          }}
        >
          &#8377;
          {grandTotal.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </Text>
      </div>

      {/* 3. PAYMENTS DONE SECTION - Subtle Soft Emerald Green Tint */}
      <div
        style={{
          width: "100%",
          backgroundColor: "#ecfdf5",
          borderTop: "1px solid #a7f3d0",
          borderBottom: "1px solid #a7f3d0",
          padding: "10px 16px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <Text
          b
          size="12px"
          css={{
            margin: 0,
            color: "#047857",
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          Payments Done
        </Text>
        {payments.length === 0 ? (
          <Text size="12px" css={{ margin: 0, color: "#059669" }}>
            No payments recorded for this month
          </Text>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {payments.map((p) => (
              <div
                key={p._id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "5px 0",
                  borderBottom: "1px dashed #a7f3d0",
                }}
              >
                <Text size="11px" css={{ margin: 0, color: "#047857" }}>
                  {moment(p.created).format("DD MMM YYYY, hh:mm A")}
                </Text>
                <Text
                  b
                  size="13px"
                  css={{
                    margin: 0,
                    color: "#047857",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  - &#8377;
                  {Number(p.amount).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 4. FINAL BALANCE / REMAINING DUES SECTION - Subtle Soft Rose (if pending) or Green (if settled) Tint */}
      <div
        style={{
          width: "100%",
          backgroundColor: finalBalance > 0 ? "#fff1f2" : "#f0fdf4",
          borderTop:
            finalBalance > 0 ? "1px solid #fecdd3" : "1px solid #bbf7d0",
          borderBottom:
            finalBalance > 0 ? "1px solid #fecdd3" : "1px solid #bbf7d0",
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text
          b
          size="13px"
          css={{
            margin: 0,
            color: finalBalance > 0 ? "#be123c" : "#15803d",
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          {finalBalance > 0
            ? "Remaining Dues Balance"
            : "Status: Fully Settled"}
        </Text>
        <Text
          b
          size="16px"
          css={{
            margin: 0,
            color: finalBalance > 0 ? "#e11d48" : "#16a34a",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 800,
          }}
        >
          &#8377;
          {finalBalance.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </Text>
      </div>
    </div>
  );
};

export default BanksLedgerContent;
